<template>
    <div>
        <div class="w-full">
            <h1 class="pw-text-black text-center !text-4xl">Pathway Pricing</h1>
            <Calendly name="GetStarted" href="https://calendly.com/d/dj2-4jd-3tw/introductory-call-pathway"
                class="flex gap-2 items-center pw-button--primary-lb p-3 px-5 rounded-lg mx-auto my-4">
                Contact us
                <Icon name="heroicons:chat-bubble-left-right-20-solid" />
            </Calendly>
            <client-only>
                <div class="rounded-lg max-w-max mx-auto my-6 flex gap-4 flex-wrap justify-center">
                    <button type="button" @click="changeTab(0)"
                        class="p-1 px-16 border border-gray-400 bg-gray-100 rounded-lg pw-text-black text-gray-900 hover:bg-white transition"
                        :class="{ 'border-primary-500 !text-primary-500 font-medium bg-white': tabIndex === 0 }">Live
                        Data framework</button>
                    <button type="button" @click="changeTab(1)"
                        class="p-1 px-16 border border-gray-400 bg-gray-100 rounded-lg pw-text-black text-gray-900 hover:bg-white transition"
                        :class="{ 'border-primary-500 !text-primary-500 font-medium bg-white': tabIndex === 1 }">AI
                        pipelines</button>
                </div>
            </client-only>
            <!-- <UTabs :items="tabs" v-model="selected"
                :ui="{ list: { base: '!w-[20rem] mt-2 gap-2 mx-auto !grid', tab: { background: 'border border-gray-200', inactive: 'pw-text-black', active: 'text-primary-500 border-primary-500' } } }"> -->
            <div v-show="tabIndex === 0">
                <div class="mt-5">


                    <div class="text-center">
                        <h2 class="!font-bold !text-2xl pw-text-black">All you need for your ETL and AI pipelines
                        </h2>
                        <div class="mt-4 max-w-3xl mx-auto">
                            <p><strong>Pathway</strong> is a scalable and robust data processing framework. Use it
                                to
                                build and
                                power
                                AI/ML applications with live data and real-time pipelines.</p>
                        </div>
                    </div>
                    <div class="flex md:flex-row flex-col gap-2 mt-8">
                        <div class="p-4 border border-gray-200 rounded-lg hover:border-gray-400 transition-colors">
                            <span
                                class="text-3xl py-2 bg-opacity-90 font-semibold pw-text-black text-center w-full block sticky top-header bg-white z-10">Community</span>
                            <div class="flex flex-col mt-2 text-center">
                                <span class="text-sm uppercase -mb-1 font-regular">Supported up to</span>
                                <span class="font-semibold text-lg">8 GB RAM - 4 cores</span>
                            </div>
                            <div class="text-center my-4 mt-[3rem]">
                                <NuxtLink to="/developers/user-guide/introduction/installation"
                                    class="pw-button--primary flex items-center justify-center text-center text-lg w-full gap-4 bg-primary-500 p-3 text-white rounded-xl transition">
                                    <span>Install it</span>
                                    <Icon name="heroicons:arrow-right-16-solid" />
                                </NuxtLink>
                            </div>
                            <div class="text-center flex gap-2 items-center justify-center text-2xl my-4">
                                <Icon name="uil:linux" />
                                <Icon name="wpf:mac-os" />
                                <Icon name="mdi:docker" />
                            </div>
                            <div class="text-center">
                                <span class="block">Free and open under BSL 1.1.</span>
                            </div>
                            <div class="text-center">
                                <span class="block">Self-hosted only.</span>
                            </div>
                            <div class="text-center">
                                <span>Packaged for pip, poetry, docker.</span>
                            </div>
                            <div class="text-center mt-4">
                                <span class="font-semibold">Deploy quickly on</span>
                                <div class="flex gap-3 items-center justify-center mt-1">
                                    <!-- <NuxtLink to="/developers/user-guide/deployment/render-deploy"
                                        class="hover:underline text-primary-500 flex gap-2 items-center">
                                        <Icon name="material-symbols:cloud" /> Render
                                    </NuxtLink> -->
                                    <NuxtLink to="/developers/user-guide/deployment/aws-fargate-deploy"
                                        class="hover:underline text-primary-500 flex gap-2 items-center">
                                        <Icon name="mdi:aws" /> AWS
                                    </NuxtLink>
                                    <NuxtLink to="/developers/user-guide/deployment/azure-aci-deploy"
                                        class="hover:underline text-primary-500 flex gap-2 items-center">
                                        <Icon name="codicon:azure" /> Azure
                                    </NuxtLink>
                                    <NuxtLink to="/developers/user-guide/deployment/gcp-deploy"
                                        class="hover:underline text-primary-500 flex gap-2 items-center">
                                        <Icon name="simple-icons:googlecloud" /> GCP
                                    </NuxtLink>
                                </div>
                            </div>
                            <hr class="my-4 border-gray-50" />
                            <div class="features-list">
                                <span class="text-lg font-semibold mt-2">What's included:</span>
                                <div class="mt-4">
                                    <span class="font-bold">20+ App templates</span>
                                </div>
                                <ul class="templates">
                                    <li>basic RAG pipelines</li>
                                    <li>document pipelines</li>
                                    <li>log monitoring</li>
                                    <li>Kafka ETL</li>
                                    <li>social media
                                        sentiment analysis</li>
                                </ul>
                                <hr class="my-4 mt-[4.75rem] border-gray-50" />
                                <span class="font-semibold mt-4 block">Core features</span>
                                <ul>
                                    <li>High-performance input/output connectors (Kafka, S3, cloud file systems,
                                        databases)</li>
                                    <li>Predefined API connectors to 300+ data sources</li>
                                    <li>REST API endpoint for serving query/answer and realtime features with
                                        sub-millisecond
                                        latency</li>
                                    <li>Python programming API (Table API)</li>
                                    <li>SQL programming API</li>
                                    <li>Incremental stream and table operations: join, filter, group-by, reduce</li>
                                    <li>Advanced join types, temporal joins, windows, and ranges</li>
                                    <li>Custom stateful reducers</li>
                                    <li>Incremental "apply" and "map-reduce"</li>
                                    <li>Support for pointer-based data structures: trees, graphs, event sequences
                                    </li>
                                    <li>User Defined Functions: call external libraries</li>
                                    <li>Make API calls from Pathway data flows</li>
                                    <li>Async data processing: call APIs, libraries, LLM services</li>
                                    <li>Data schema support (Python/mypy compatible typing)</li>
                                    <li>True streaming data processing engine in Rust</li>
                                    <li>Same engine for streaming and batch workloads</li>
                                    <li>Same code logic for streaming and for backfilling</li>
                                    <li>Parallelization with multi-processing</li>
                                    <li>Data persistence with S3 storage</li>
                                    <li>Fully interactive execution with streaming & live data sources</li>
                                    <li>Develop and run in Jupyter notebooks (also in streaming)</li>
                                    <li>Data table introspection & debugging in streaming</li>
                                    <li>Out-of-the-box code completion supported by IDEs (VS Code, PyCharm, ...)
                                    </li>
                                    <li>Dataflow and schema validation at compile time</li>
                                    <li>Schema autodetection from data samples</li>
                                    <li>Exception handling at runtime</li>
                                </ul>
                                <span class="font-semibold mt-4 block">AI Toolkits</span>
                                <ul>
                                    <li>Libraries for time-series operations, sampling, and filters</li>
                                    <li>LLM extension pack</li>
                                    <li>Unstructured data parsing toolkit (data source sync, parsing, extraction,
                                        indexing)</li>
                                    <li>Advanced data indexing built-in (vector with HNSW, BM24, hybrid)</li>
                                    <li>Support for custom indexing with knowledge graph and summarization
                                        techniques
                                    </li>
                                    <li>Support for fully local and API-based ML models</li>
                                    <li>Built-in support for temporal graph data</li>
                                    <li>Library for classification and clustering</li>
                                </ul>
                                <span class="font-semibold mt-4 block">Input connectors</span>
                                <ul class="list-disc marker:text-blue-600">
                                    <li>Kafka</li>
                                    <li>BigQuery</li>
                                    <li>PostgreSQL</li>
                                    <li>Elastic Search</li>
                                    <li>http</li>
                                    <li>JSON Lines</li>
                                    <li>Redpanda</li>
                                    <li>Logstash</li>
                                    <li>Slack</li>
                                    <li>File System</li>
                                    <li>Google PubSub</li>
                                    <li>Custom Python connector (APIs and other destinations)</li>
                                </ul>
                                <span class="font-semibold mt-4 block">Support & Deployment</span>
                                <ul>
                                    <li>Self hosted setup: Run Pathway on your own machine or cloud</li>
                                    <li>Community support</li>
                                </ul>
                            </div>
                        </div>
                        <div class="p-4 border border-gray-200 rounded-lg hover:border-gray-400 transition-colors">
                            <span
                                class="text-3xl py-2 bg-opacity-90 font-semibold pw-text-black text-center w-full block sticky top-header bg-white z-10">Scale</span>
                            <div class="flex flex-col mt-2 text-center">
                                <span class="text-sm uppercase ">Supported up to</span>
                                <USelect :options="scaleOptions" model-value="16 GB RAM - 4 cores (Free)" size="xl"
                                    class="font-bold" :ui="{ color: { white: { outline: 'text-gray-700' } } }"
                                    v-model="scaleSelected" />
                            </div>
                            <div class="text-center my-4 mt-[1.75rem]">
                                <NuxtLink to="/get-license"
                                    class="pw-button--primary flex items-center justify-center text-center text-lg w-full gap-4 bg-primary-500 p-3 text-white rounded-xl transition"
                                    v-if="scaleSelected.toString() == scaleOptions[0].toString()">
                                    <span>Get License</span>
                                    <Icon name="heroicons:arrow-right-16-solid" />
                                </NuxtLink>
                                <Calendly name="getintouch"
                                    href="https://calendly.com/d/dj2-4jd-3tw/introductory-call-pathway"
                                    class="pw-button--primary flex items-center justify-center text-center text-lg w-full gap-4 bg-primary-500 p-3 text-white rounded-xl transition"
                                    v-else>
                                    <span>Get in touch</span>
                                    <Icon name="heroicons:arrow-right-16-solid" />
                                </Calendly>
                            </div>
                            <div class="text-center flex gap-2 items-center justify-center text-2xl my-4">
                                <Icon name="uil:linux" />
                                <Icon name="wpf:mac-os" />
                                <Icon name="mdi:docker" />
                            </div>
                            <div class="text-center">
                                <span class="block">Free or paid, with license key.</span>
                            </div>
                            <div class="text-center">
                                <span class="block">Self-hosted only.</span>
                            </div>
                            <div class="text-center">
                                <span>Packaged for pip, poetry, docker.</span>
                            </div>
                            <div class="text-center mt-4">
                                <span class="font-semibold">Deploy quickly on</span>
                                <div class="flex gap-3 items-center justify-center mt-1">
                                    <!-- <NuxtLink to="/developers/user-guide/deployment/render-deploy"
                                        class="hover:underline text-primary-500 flex gap-2 items-center">
                                        <Icon name="material-symbols:cloud" /> Render
                                    </NuxtLink> -->
                                    <NuxtLink to="/developers/user-guide/deployment/aws-fargate-deploy"
                                        class="hover:underline text-primary-500 flex gap-2 items-center">
                                        <Icon name="mdi:aws" /> AWS
                                    </NuxtLink>
                                    <NuxtLink to="/developers/user-guide/deployment/azure-aci-deploy"
                                        class="hover:underline text-primary-500 flex gap-2 items-center">
                                        <Icon name="codicon:azure" /> Azure
                                    </NuxtLink>
                                    <NuxtLink to="/developers/user-guide/deployment/gcp-deploy"
                                        class="hover:underline text-primary-500 flex gap-2 items-center">
                                        <Icon name="simple-icons:googlecloud" /> GCP
                                    </NuxtLink>
                                </div>
                            </div>
                            <hr class="my-4 border-gray-50" />
                            <div class="features-list">
                                <span class="text-lg font-semibold mt-2">Everything in Community, plus:</span>
                                <div class="mt-4">
                                    <span class="font-bold">Extra App templates</span>
                                </div>
                                <ul class="templates">
                                    <li>high-accuracy RAG</li>
                                    <li>Sharepoint AI search</li>
                                    <li>Delta lake ETL</li>
                                    <li>monitored instances</li>
                                </ul>
                                <hr class="my-4 mt-[6.5rem] border-gray-50" />
                                <span class="font-semibold mt-4 block">Advanced features</span>
                                <ul>
                                    <li>Enterprise data source connectors for Sharepoint, Delta Table, Iceberg (more coming
                                        soon)
                                    </li>
                                    <li>Monitoring and traces for Pathway Instances</li>
                                    <li>OpenTelemetry compatible</li>
                                    <li>Grafana integration</li>
                                    <li class="flex flex-col">
                                        <span>Business Support with tickets</span>
                                        <span class="text-sm">1-business day reply time for paid tier
                                            customers</span>
                                    </li>
                                    <li>Free 1st consultation call for pipeline design for your RAG, streaming and
                                        ETL
                                        use case</li>
                                    <li class="flex flex-col">
                                        <span>#DeveloperAssist program</span>
                                        <span class="text-sm">
                                            <Modal name="Form" class="text-primary-500 hover:underline">Request now
                                            </Modal>
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="p-4 border border-gray-200 rounded-lg hover:border-gray-400 transition-colors">
                            <span
                                class="text-3xl py-2 bg-opacity-90 font-semibold pw-text-black text-center w-full block sticky top-header bg-white z-10">Enterprise</span>
                            <div class="flex flex-col mt-2 text-center">
                                <span class="text-sm uppercase -mb-1">Supported up to</span>
                                <span class="font-semibold text-lg">24 TB RAM - 128 cores</span>
                                <span class="text-sm">x 40 nodes</span>
                            </div>
                            <div class="text-center my-4  mt-[1.75rem]">
                                <Calendly name="getintouch"
                                    href="https://calendly.com/d/dj2-4jd-3tw/introductory-call-pathway"
                                    class="pw-button--primary flex items-center justify-center text-center text-lg w-full gap-4 bg-primary-500 p-3 text-white rounded-xl transition">
                                    <span>Get in touch</span>
                                    <Icon name="heroicons:arrow-right-16-solid" />
                                </Calendly>
                            </div>
                            <div class="text-center flex gap-2 items-center justify-center text-2xl my-4">
                                <Icon name="uil:linux" />
                                <Icon name="wpf:mac-os" />
                                <Icon name="mdi:docker" />
                                <Icon name="mdi:kubernetes" />
                            </div>
                            <div class="text-center">
                                <span class="block">Pathway Enterprise Licensing.</span>
                            </div>
                            <div class="text-center">
                                <span class="block">Self-hosted and managed.</span>
                            </div>
                            <div class="text-center">
                                <span>Private package registry.</span>
                            </div>
                            <div class="text-center mt-4">
                                <span class="font-semibold">Deploy on cloud of choice</span>
                                <div class="flex gap-3 items-center justify-center mt-1">
                                    <span class="flex gap-2 items-center">
                                        <Icon name="mdi:aws" /> AWS
                                    </span>
                                    <span class="flex gap-2 items-center">
                                        <Icon name="codicon:azure" /> Azure
                                    </span>
                                    <span class="flex gap-2 items-center">
                                        <Icon name="simple-icons:googlecloud" /> GCP
                                    </span>
                                    <span class="flex gap-2 items-center">
                                        <Icon name="simple-icons:intel" /> Intel
                                    </span>
                                </div>
                            </div>
                            <hr class="my-4 border-gray-50" />
                            <div class="features-list">
                                <span class="text-lg font-semibold mt-2">Everything in Scale, plus:</span>
                                <div class="mt-4">
                                    <span class="font-bold">Industry solutions</span>
                                </div>
                                <ul class="templates">
                                    <li>real-time GPS data analytics</li>
                                    <li>logistics</li>
                                    <li>automotive</li>
                                    <li>IoT analytics</li>
                                    <li>fraud detection</li>
                                    <li>RAG for sales and marketing</li>
                                    <li>search in slide decks</li>
                                </ul>
                                <hr class="my-4 border-gray-50" />
                                <span class="font-semibold mt-4 block">Enterprise features</span>
                                <ul>
                                    <li>Horizontal Scalability</li>
                                    <li>High availability with hot failover</li>
                                    <li>Pathway Helm Chart models</li>
                                    <li>Kubernetes deployment guides</li>
                                    <li>Connectors for custom messaging formats (MQTT/IoT, ...)</li>
                                    <li>Pathway Visual Explorer: Live Dashboards with Pathway, including geospatial
                                        data
                                        viz</li>
                                    <li>Queryable historical data snapshots</li>
                                    <li>Real-time geospatial & trajectory mining library (GPS traces)</li>
                                    <li>Support for data schema & code schema versioning</li>
                                    <li>Pathway managed services and hosting</li>
                                    <li>Solutioning for industry use cases</li>
                                    <li>Made-to-measure data pipelines</li>
                                    <li>Access to professional services</li>
                                    <li>Multi-zone SLA</li>
                                    <li>24/7 phone support</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-show="tabIndex === 1">
                <div class="mt-5">
                    <div class="text-center">
                        <h2 class="!font-bold !text-2xl pw-text-black">Pathway RAG pipelines: Pricing</h2>
                        <div class="mt-4 max-w-3xl mx-auto">
                            <p><strong>Pathway RAG</strong> pipelines offer flexible plans to suit
                                collections of any size.
                                The "Pilot" plan includes onboarding up to 100,000 indexed pages and allows 1,000
                                data retrievals
                                per day. For larger document repositories, the Enterprise Pilot plan can handle up
                                to one million
                                pages. You can easily get started with Pathway RAG.</p>
                        </div>
                    </div>
                    <div class="flex md:flex-row flex-col gap-2 mt-8">
                        <div
                            class="p-4 border border-gray-200 rounded-lg hover:border-gray-400 transition-colors grow md:basis-1/4">
                            <span
                                class="text-3xl py-2 bg-opacity-90 font-semibold pw-text-black text-center w-full block sticky top-header bg-white z-10">Department
                                Pilot</span>
                            <div class="flex flex-col mt-2 text-center">
                                <span class="text-sm uppercase mb-1 font-regular">$999 Onboarding*</span>
                                <div>
                                    <span class="font-semibold text-4xl">$499</span>
                                    <span>/month</span>
                                </div>
                            </div>
                            <div class="text-center my-4 mt-[3rem]">
                                <!-- <NuxtLink to="/developers/templates/run-a-template"
                                    class="pw-button--primary flex items-center justify-center text-center text-lg w-full gap-4 bg-primary-500 p-3 text-white rounded-xl transition">
                                    <span>Get started</span>
                                    <Icon name="heroicons:arrow-right-16-solid" />
                                </NuxtLink> -->
                                <Calendly name="getintouch"
                                    href="https://calendly.com/d/dj2-4jd-3tw/introductory-call-pathway"
                                    class="pw-button--primary flex items-center justify-center text-center text-lg w-full gap-4 bg-primary-500 p-3 text-white rounded-xl transition">
                                    <span>Get in touch</span>
                                    <Icon name="heroicons:arrow-right-16-solid" />
                                </Calendly>
                                <NuxtLink to="/developers/templates/run-a-template"
                                    class="text-primary-500 hover:underline mt-4 flex items-center gap-1 justify-center text-sm">
                                    See
                                    developer docs
                                    <Icon name="heroicons:arrow-right-16-solid" class="mb-[3px]" />
                                </NuxtLink>

                            </div>
                            <div class="text-center">
                                <span class="text-xs font-bold uppercase">Contract duration</span>
                                <span class="block">30 days, 60 days, 90 days</span>
                            </div>
                            <!-- TABLE -->
                            <div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Indexed documents limit</span>
                                    <span>100,000 pages</span>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Included retrievals per day</span>
                                    <span>1,000</span>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Supported Cloud Providers</span>
                                    <ul>
                                        <li class="flex items-center gap-2">
                                            <Icon name="mdi:aws" /> AWS
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="mdi:microsoft-azure" /> Azure
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="simple-icons:googlecloud" /> Google Cloud
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="heroicons:home-20-solid" /> On-premises
                                        </li>
                                    </ul>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Support Level</span>
                                    <ul class="features-list">
                                        <li>#DeveloperAssist program</li>
                                        <li>Business support with tickets 1 business day reply time</li>
                                        <li>Free first consultation call for pipeline design on your case</li>
                                    </ul>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Data sources supported</span>
                                    <ul>
                                        <li class="flex items-center gap-2">
                                            <Icon name="simple-icons:googledrive" /> Google Drive
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="mdi:microsoft-sharepoint" /> Microsoft SharePoint
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="heroicons:document-solid" /> Local File System
                                        </li>
                                    </ul>
                                </div>

                            </div>
                            <!-- END OF TABLE -->
                        </div>

                        <div
                            class="p-4 border border-gray-200 rounded-lg hover:border-gray-400 transition-colors grow md:basis-1/4">
                            <span
                                class="text-3xl py-2 bg-opacity-90 font-semibold pw-text-black text-center w-full block sticky top-header bg-white z-10">Scale</span>
                            <div class="flex flex-col mt-2 text-center">
                                <span class="text-sm uppercase mb-1 font-regular">$1999 Onboarding*</span>
                                <div>
                                    <span class="font-semibold text-4xl">$499</span>
                                    <span>/month</span>
                                </div>
                            </div>
                            <div class="text-center my-4 mt-[3rem]">
                                <Calendly name="getintouch"
                                    href="https://calendly.com/d/dj2-4jd-3tw/introductory-call-pathway"
                                    class="pw-button--primary flex items-center justify-center text-center text-lg w-full gap-4 bg-primary-500 p-3 text-white rounded-xl transition">
                                    <span>Get in touch</span>
                                    <Icon name="heroicons:arrow-right-16-solid" />
                                </Calendly>
                                <NuxtLink to="/developers/templates/run-a-template"
                                    class="text-primary-500 hover:underline mt-4 flex items-center gap-1 justify-center text-sm">
                                    See
                                    developer docs
                                    <Icon name="heroicons:arrow-right-16-solid" class="mb-[3px]" />
                                </NuxtLink>
                            </div>
                            <div class="text-center">
                                <span class="text-xs font-bold uppercase">Contract duration</span>
                                <span class="block">12 months</span>
                            </div>
                            <!-- TABLE -->
                            <div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Indexed documents limit</span>
                                    <span>1,000,000 pages</span>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Included retrievals per day</span>
                                    <span>1,000</span>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Supported Cloud Providers</span>
                                    <ul>
                                        <li class="flex items-center gap-2">
                                            <Icon name="mdi:aws" /> AWS
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="mdi:microsoft-azure" /> Azure
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="simple-icons:googlecloud" /> Google Cloud
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="heroicons:home-20-solid" /> On-premises via SSH
                                        </li>
                                    </ul>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Support Level</span>
                                    <ul class="features-list">
                                        <li>#DeveloperAssist program</li>
                                        <li>Business support with tickets 1 business day reply time</li>
                                        <li>Free first consultation call for pipeline design on your case</li>
                                    </ul>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Data sources supported</span>
                                    <ul>
                                        <li class="flex items-center gap-2">
                                            <Icon name="simple-icons:googledrive" /> Google Drive
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="mdi:microsoft-sharepoint" /> Microsoft SharePoint
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="heroicons:document-solid" /> Local File System
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="heroicons:server-solid" />FTP
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="simple-icons:amazon" /> S3
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="heroicons:circle-stack-16-solid" />Min.IO
                                        </li>
                                    </ul>
                                </div>

                            </div>
                            <!-- END OF TABLE -->
                        </div>
                        <div
                            class="p-4 border border-gray-200 rounded-lg hover:border-gray-400 transition-colors grow md:basis-1/4">
                            <span
                                class="text-3xl py-2 bg-opacity-90 font-semibold pw-text-black text-center w-full block sticky top-header bg-white z-10">Enterprise</span>
                            <div class="flex flex-col mt-8 text-center">
                                <div>
                                    <span class="font-semibold text-4xl">Contact Us</span>
                                </div>
                            </div>
                            <div class="text-center my-4  mt-[3rem]">
                                <Calendly name="getintouch"
                                    href="https://calendly.com/d/dj2-4jd-3tw/introductory-call-pathway"
                                    class="pw-button--primary flex items-center justify-center text-center text-lg w-full gap-4 bg-primary-500 p-3 text-white rounded-xl transition">
                                    <span>Contact Us</span>
                                    <Icon name="heroicons:arrow-right-16-solid" />
                                </Calendly>
                            </div>
                            <div class="text-center">
                                <span class="text-xs font-bold uppercase">Contract duration</span>
                                <span class="block">12 months</span>
                            </div>
                            <!-- TABLE -->
                            <div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-[3.3rem]">
                                    <span class="font-bold">Indexed documents limit</span>
                                    <span>10,000,000 pages</span>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Up to 1000 retrievals / minute</span>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-11">
                                    <span class="font-bold">Supported Cloud Providers</span>
                                    <ul>
                                        <li class="flex items-center gap-2">
                                            <Icon name="mdi:aws" /> AWS
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="mdi:microsoft-azure" /> Azure
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="simple-icons:googlecloud" /> Google Cloud
                                        </li>
                                        <li class="flex items-center gap-2">
                                            <Icon name="heroicons:home-20-solid" /> On-premises via SSH
                                        </li>
                                    </ul>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Support Level</span>
                                    <ul class="features-list">
                                        <li>Everything from the previous tiers</li>
                                        <li>Dedicated support</li>
                                        <li>Access to professional services</li>
                                        <li>Multi-zone SLA</li>
                                        <li>24/7 phone support</li>
                                    </ul>
                                </div>
                                <div class="flex flex-col border-t pt-4 border-gray-50  mt-4">
                                    <span class="font-bold">Data sources supported</span>
                                    <ul class="features-list">
                                        <li class="flex items-center gap-2">
                                            All standard data sources
                                        </li>
                                        <li class="flex items-center gap-2">
                                            Tailor-made custom data sources support
                                        </li>
                                    </ul>
                                </div>

                            </div>
                        </div>
                    </div>
                    <span class="text-xs text-center block mt-2">
                        * if converting from a pilot to scale, no need to re-onboard
                    </span>
                </div>
            </div>
            <!-- </UTabs> -->
        </div>


    </div>
</template>

<script setup>
const tabs = [{
    label: "Pathway Core",
    slot: "live-data-framework",
    href: "live-data-framework"
}, {
    label: "RAG Solution",
    href: "ai-pipelines",
    slot: "rag"
}]


const indexMap = {
    "live-data-framework": 0,
    "ai-pipelines": 1,
    "rag-solution": 1
}
const route = useRoute()
const router = useRouter()

const tabIndex = (route.query.tab && indexMap[route.query.tab]) ? ref(indexMap[route.query.tab]) : ref(0)

const changeTab = (index) => {
    tabIndex.value = index
    router.replace({
        query: { tab: tabs[index].href }
    })
}

// const route = useRoute()
// const router = useRouter()

// const selected = computed({
//     get () {
//         const index = tabs.findIndex((item) => item.label === route.query.tab)
//         if (index === -1) {
//             return 0
//         }
//         return index
//     },
//     set (value) {
//         router.replace({
//             query: { tab: tabs[value].label },
//             //hash: '#index' 
//         })
//     }
// })

const scaleOptions = ["16 GB RAM - 4 cores (Free)", "2 TB RAM - 16 cores (Paid)"]
const scaleSelected = ref(scaleOptions[0])
</script>

<style scoped>
.features-list ul {
    list-style-type: none;
    overflow-x: visible;
}

.features-list li::marker {
    display: none;
}

.features-list li {
    position: relative;
    padding-left: 1.5rem;
    margin: 0.3rem 0;
}

.features-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.24rem;
    width: 1em;
    height: 1em;
    background-repeat: no-repeat;
    background-image: url('data:image/svg+xml,<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="8" fill="%23DADAFF"/><g clip-path="url(%23clip0_219_58)"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.9778 5.12949C12.095 5.24669 12.1608 5.40564 12.1608 5.57136C12.1608 5.73709 12.095 5.89603 11.9778 6.01324L7.29325 10.6978C7.23135 10.7597 7.15785 10.8089 7.07695 10.8424C6.99606 10.8759 6.90936 10.8931 6.8218 10.8931C6.73423 10.8931 6.64753 10.8759 6.56664 10.8424C6.48574 10.8089 6.41224 10.7597 6.35034 10.6978L4.02284 8.37074C3.96314 8.31308 3.91553 8.24412 3.88277 8.16787C3.85002 8.09161 3.83278 8.0096 3.83205 7.92661C3.83133 7.84363 3.84715 7.76133 3.87857 7.68452C3.91 7.60771 3.95641 7.53792 4.01509 7.47924C4.07377 7.42056 4.14355 7.37415 4.22037 7.34273C4.29718 7.3113 4.37948 7.29549 4.46246 7.29621C4.54545 7.29693 4.62746 7.31417 4.70371 7.34693C4.77997 7.37968 4.84893 7.4273 4.90659 7.48699L6.82159 9.40199L11.0937 5.12949C11.1517 5.07141 11.2206 5.02534 11.2965 4.9939C11.3723 4.96247 11.4536 4.94629 11.5358 4.94629C11.6179 4.94629 11.6992 4.96247 11.775 4.9939C11.8509 5.02534 11.9198 5.07141 11.9778 5.12949Z" fill="%230808F9"/></g><defs><clipPath id="clip0_219_58"><rect width="10" height="10" fill="white" transform="translate(3 3)"/></clipPath></defs></svg>')
}

.features-list .templates li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.24rem;
    width: 2em;
    height: 2em;
    background-repeat: no-repeat;
    background-image: url('data:image/svg+xml,<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="8" fill="%23DADAFF"/><path d="M8.279 3.01181C8.19403 2.96327 8.09786 2.93774 8 2.93774C7.90214 2.93774 7.80597 2.96327 7.721 3.01181L4.1135 5.07281L8 7.34831L11.8865 5.07281L8.279 3.01181ZM12.5 6.01781L8.5625 8.32256V12.8263L12.2165 10.7383C12.3026 10.6891 12.3742 10.6181 12.4239 10.5323C12.4737 10.4466 12.5 10.3492 12.5 10.2501V6.01781ZM7.4375 12.8263V8.32256L3.5 6.01781V10.2501C3.5 10.4526 3.608 10.6386 3.7835 10.7383L7.4375 12.8263Z" fill="%230808F9"/></svg>')
}
</style>
